import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from '@mui/material';
import { Breadcrumbs } from 'pvs-design-system';
import { PAGE_TITLE_HEADER_TEXT_ID, ROUTES } from 'constants/constants';
import { Link, useLocation } from 'react-router-dom';
import { Notify } from 'components/Notifications/Notifications';
import { useDownloadImportErrorFileMutation, useGetImportLogsQuery } from 'api';
import { LinkImportStatus, LinkImportLogDetails } from '../../types/ImportLog';

// Add this mapping at the top of your file
const statusLabelMap: Record<LinkImportStatus | string, string> = {
  [LinkImportStatus.Success]: 'Success',
  [LinkImportStatus.PartialError]: 'Partial Error',
  [LinkImportStatus.Error]: 'Error',
  // fallback if backend ever sends string instead of number
  Success: 'Success',
  PartialError: 'Partial Error',
  Error: 'Error',
};

const ImportLogPage: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  const { data } = useGetImportLogsQuery({
    pageIndex: pageNumber,
    pageSize,
  });

  const logs: LinkImportLogDetails[] = data?.linkImportLogs ?? [];
  const totalCount: number = data?.totalRecords ?? 0;

  const [downloadErrorFile] = useDownloadImportErrorFileMutation();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.toastMessage) {
      if (location.state.toastType === 'error') {
        Notify.error(location.state.toastMessage);
      } else {
        Notify.success(location.state.toastMessage);
      }
    }
  }, [location.state]);

  const handleDownload = (importId: string) => {
    downloadErrorFile(importId);
  };

  return (
    <>
      <Breadcrumbs
        links={[
          {
            to: ROUTES.links,
            isMobileLink: true,
            linkText: 'Link Manager',
            component: Link,
          },
          {
            to: ROUTES.links,
            linkText: 'Link Manager',
            component: Link,
          },
          {
            href: '',
            linkText: 'Session Log',
            component: Link,
          },
        ]}
      />
      <Typography
        id={PAGE_TITLE_HEADER_TEXT_ID}
        variant="h1"
        marginTop="40px"
        textAlign="left"
      >
        Import Log
      </Typography>

      <Box sx={{ maxWidth: 2300, margin: '0 auto', px: 2, mt: 2 }} p={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tool Name</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Error Log File</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => {
              // Helper variables to avoid nested ternaries
              let borderColor = '#B4BC08';
              let bgcolor = '#F9FBE2';

              if (
                log.status === LinkImportStatus.Error ||
                log.status === 'Error'
              ) {
                borderColor = '#BF0366'; // hot pink
                bgcolor = '#FDECF5';
              } else if (
                log.status === LinkImportStatus.PartialError ||
                log.status === 'PartialError'
              ) {
                borderColor = '#FFA613';
                bgcolor = '#FFF8E7';
              }

              return (
                <TableRow key={log.importId}>
                  <TableCell>{log.toolId ?? '-'}</TableCell>
                  <TableCell>{log.userId}</TableCell>
                  <TableCell>{log.importDate}</TableCell>
                  <TableCell>{log.importTime}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        border: '1.5px solid',
                        borderColor,
                        bgcolor,
                        color: 'black',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        minWidth: '80px',
                        borderRadius: '6px',
                      }}
                    >
                      {statusLabelMap[log.status] ?? log.status}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {log.errorFileName ? (
                      <Box
                        component="button"
                        onClick={() => handleDownload(log.importId)}
                        sx={{
                          background: 'none',
                          border: 'none',
                          color: 'primary.main',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          font: 'inherit',
                          p: 0,
                        }}
                      >
                        {log.errorFileName}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          page={pageNumber}
          onPageChange={(e, newPage) => setPageNumber(newPage)}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPageNumber(0);
          }}
        />
      </Box>
    </>
  );
};

export default ImportLogPage;
